let storedBulkData = null;
let storedSelectedRows = null;

// Setter functions to update the stored values
export const setStoredBulkData = data => {
  storedBulkData = data;
};
export const setStoredSelectedRows = rows => {
  storedSelectedRows = rows;
};

/**
 * The initial state structure for selections
 * @returns {Object} Empty selection state
 */
export const getInitialSelectionState = () => ({
  sections: {},
  // Organized by section number
  trackerTypes: {},
  // Organized by tracker type ID
  count: 0 // Total selected rows
});

// Check if a tracker row is selected

export const isTrackerRowSelected = (selectedRows, data) => {
  if (!selectedRows || !selectedRows.sections || !data) return false;
  const trackerRowId = data.tracker_row_id?.toString();
  if (!trackerRowId) return false;

  // Check if this tracker row exists in any section
  return Object.values(selectedRows.sections).some(section => section && section.selectedRows && section.selectedRows[trackerRowId]);
};

// Handle selecting/unselecting  a tracker (add or remove from selection)

export const handleSelectTracker = data => {
  // Ensure we have valid selection state
  const baseSelection = storedSelectedRows && typeof storedSelectedRows === 'object' ? storedSelectedRows : getInitialSelectionState();

  // If data is invalid, return current selection unchanged
  if (!data || !data.tracker_row_id) {
    return baseSelection;
  }

  // Clone the current selection to create a new state
  const newSelection = {
    sections: {
      ...baseSelection.sections
    } || {},
    trackerTypes: {
      ...baseSelection.trackerTypes
    } || {},
    count: baseSelection.count || 0
  };
  const trackerRowId = data.tracker_row_id.toString();
  const trackerTypeId = data.tracker_type_id.toString();
  const section = data.section.toString();

  // Get section and tracker type keys
  const sectionKey = `section_${section}`;
  const trackerTypeKey = `trackerType_${trackerTypeId}`;

  // Calculate total counts for the section and tracker type
  let sectionTotalCount = 0;
  let trackerTypeTotalCount = 0;

  // Count total rows in this section and tracker type
  if (storedBulkData && storedBulkData.piers) {
    for (const rowId in storedBulkData.piers) {
      const pierArray = storedBulkData.piers[rowId];
      if (!pierArray || !pierArray.length) continue;
      const firstPier = pierArray[0];
      if (firstPier.section.toString() === section) {
        sectionTotalCount++;
      }
      if (firstPier.tracker_type_id.toString() === trackerTypeId) {
        trackerTypeTotalCount++;
      }
    }
  }

  // Check if already selected
  const isSelected = isTrackerRowSelected(newSelection, data);

  // If already selected, remove it
  if (isSelected) {
    // Remove from section
    if (newSelection.sections[sectionKey] && newSelection.sections[sectionKey].selectedRows && newSelection.sections[sectionKey].selectedRows[trackerRowId]) {
      delete newSelection.sections[sectionKey].selectedRows[trackerRowId];
      newSelection.sections[sectionKey].selectedCount--;

      // If section is now empty, clean it up
      if (newSelection.sections[sectionKey].selectedCount <= 0) {
        delete newSelection.sections[sectionKey];
      }
    }

    // Remove from tracker type
    if (newSelection.trackerTypes[trackerTypeKey] && newSelection.trackerTypes[trackerTypeKey].selectedRows && newSelection.trackerTypes[trackerTypeKey].selectedRows[trackerRowId]) {
      delete newSelection.trackerTypes[trackerTypeKey].selectedRows[trackerRowId];
      newSelection.trackerTypes[trackerTypeKey].selectedCount--;

      // If tracker type is now empty, clean it up
      if (newSelection.trackerTypes[trackerTypeKey].selectedCount <= 0) {
        delete newSelection.trackerTypes[trackerTypeKey];
      }
    }

    // Decrement count
    newSelection.count = Math.max(0, newSelection.count - 1);
  }
  // Not selected, add it
  else {
    // Get piers and tt data from bulkData if available
    const piers = storedBulkData?.piers?.[trackerRowId] || [];
    const tt = storedBulkData?.tt?.[trackerRowId] || [];

    // Add to section
    if (!newSelection.sections[sectionKey]) {
      newSelection.sections[sectionKey] = {
        totalCount: sectionTotalCount,
        selectedCount: 0,
        selectedRows: {}
      };
    }

    // Make sure selectedRows exists
    if (!newSelection.sections[sectionKey].selectedRows) {
      newSelection.sections[sectionKey].selectedRows = {};
    }
    newSelection.sections[sectionKey].selectedRows[trackerRowId] = {
      piers,
      tt
    };
    newSelection.sections[sectionKey].selectedCount = (newSelection.sections[sectionKey].selectedCount || 0) + 1;

    // Add to tracker type
    if (!newSelection.trackerTypes[trackerTypeKey]) {
      newSelection.trackerTypes[trackerTypeKey] = {
        totalCount: trackerTypeTotalCount,
        selectedCount: 0,
        selectedRows: {}
      };
    }

    // Make sure selectedRows exists
    if (!newSelection.trackerTypes[trackerTypeKey].selectedRows) {
      newSelection.trackerTypes[trackerTypeKey].selectedRows = {};
    }
    newSelection.trackerTypes[trackerTypeKey].selectedRows[trackerRowId] = {
      piers,
      tt
    };
    newSelection.trackerTypes[trackerTypeKey].selectedCount = (newSelection.trackerTypes[trackerTypeKey].selectedCount || 0) + 1;

    // Increment count
    newSelection.count = (newSelection.count || 0) + 1;
  }
  return newSelection;
};

//Handle select all functionality

export const handleSelectAll = async () => {
  // Initialize with empty selection state
  const newSelection = getInitialSelectionState();
  if (!storedBulkData || !storedBulkData.piers) return newSelection;

  // Track total counts
  const sectionCounts = {};
  const trackerTypeCounts = {};

  // Process all piers to build the selection
  for (const trackerRowId in storedBulkData.piers) {
    const pierArray = storedBulkData.piers[trackerRowId];
    if (!pierArray || !pierArray.length) continue;

    // Get data from first pier
    const firstPier = pierArray[0];
    const section = firstPier.section.toString();
    const trackerTypeId = firstPier.tracker_type_id.toString();

    // Create keys
    const sectionKey = `section_${section}`;
    const trackerTypeKey = `trackerType_${trackerTypeId}`;

    // Initialize counts if needed
    sectionCounts[sectionKey] = (sectionCounts[sectionKey] || 0) + 1;
    trackerTypeCounts[trackerTypeKey] = (trackerTypeCounts[trackerTypeKey] || 0) + 1;

    // Get tt data
    const tt = storedBulkData.tt?.[trackerRowId] || [];

    // Add to section
    if (!newSelection.sections[sectionKey]) {
      newSelection.sections[sectionKey] = {
        totalCount: 0,
        selectedCount: 0,
        selectedRows: {}
      };
    }
    newSelection.sections[sectionKey].selectedRows[trackerRowId] = {
      piers: pierArray,
      tt
    };
    newSelection.sections[sectionKey].selectedCount++;

    // Add to tracker type
    if (!newSelection.trackerTypes[trackerTypeKey]) {
      newSelection.trackerTypes[trackerTypeKey] = {
        totalCount: 0,
        selectedCount: 0,
        selectedRows: {}
      };
    }
    newSelection.trackerTypes[trackerTypeKey].selectedRows[trackerRowId] = {
      piers: pierArray,
      tt
    };
    newSelection.trackerTypes[trackerTypeKey].selectedCount++;

    // Increment count
    newSelection.count++;
  }

  // Set totalCount values
  for (const sectionKey in newSelection.sections) {
    newSelection.sections[sectionKey].totalCount = sectionCounts[sectionKey] || 0;
  }
  for (const trackerTypeKey in newSelection.trackerTypes) {
    newSelection.trackerTypes[trackerTypeKey].totalCount = trackerTypeCounts[trackerTypeKey] || 0;
  }
  return newSelection;
};

// Handle  select/unselect section 

export const handleSectionSelection = (sectionNumbers, prevSelection = []) => {
  // Ensure we have valid selection state
  const baseSelection = storedSelectedRows && typeof storedSelectedRows === 'object' ? storedSelectedRows : getInitialSelectionState();

  // Clone the current selection to create a new state
  const newSelection = {
    sections: {
      ...baseSelection.sections
    } || {},
    trackerTypes: {
      ...baseSelection.trackerTypes
    } || {},
    count: baseSelection.count || 0
  };
  if (!storedBulkData || !storedBulkData.piers) {
    return newSelection;
  }

  // Handle deselection if prevSection is specified and exists in selections
  if (Array.isArray(prevSelection) && prevSelection.length > 0) {
    prevSelection.forEach(prevSection => {
      const sectionKey = `section_${prevSection}`;

      // If this section exists in the selection, remove it completely
      if (newSelection.sections && newSelection.sections[sectionKey]) {
        const rowsToRemove = Object.keys(newSelection.sections[sectionKey].selectedRows || {});
        const rowCount = rowsToRemove.length;

        // Remove from tracker types
        rowsToRemove.forEach(trackerRowId => {
          const pierArray = storedBulkData.piers[trackerRowId];
          if (pierArray && pierArray.length > 0) {
            const firstPier = pierArray[0];
            const trackerTypeKey = `trackerType_${firstPier.tracker_type_id.toString()}`;
            if (newSelection.trackerTypes && newSelection.trackerTypes[trackerTypeKey] && newSelection.trackerTypes[trackerTypeKey].selectedRows) {
              delete newSelection.trackerTypes[trackerTypeKey].selectedRows[trackerRowId];
              newSelection.trackerTypes[trackerTypeKey].selectedCount--;
              if (newSelection.trackerTypes[trackerTypeKey].selectedCount <= 0) {
                delete newSelection.trackerTypes[trackerTypeKey];
              }
            }
          }
        });

        // Remove the entire section
        delete newSelection.sections[sectionKey];

        // Update count
        newSelection.count = Math.max(0, newSelection.count - rowCount);
      }
    });
  }
  // Return early if no sections to add
  // if (!sectionNumbers || sectionNumbers.length === 0) {
  //   return newSelection;
  // }

  // Only proceed with selection if there are section numbers to select
  if (!sectionNumbers || sectionNumbers.length === 0) {
    return newSelection;
  }

  // Track total counts for sections and tracker types
  const sectionCounts = {};
  const trackerTypeCounts = {};

  // First, calculate total counts for sections and tracker types
  for (const trackerRowId in storedBulkData.piers) {
    const pierArray = storedBulkData.piers[trackerRowId];
    if (!pierArray || !pierArray.length) continue;
    const firstPier = pierArray[0];
    const section = firstPier.section;
    const trackerTypeId = firstPier.tracker_type_id.toString();
    const sectionKey = `section_${section}`;
    const trackerTypeKey = `trackerType_${trackerTypeId}`;
    sectionCounts[sectionKey] = (sectionCounts[sectionKey] || 0) + 1;
    trackerTypeCounts[trackerTypeKey] = (trackerTypeCounts[trackerTypeKey] || 0) + 1;
  }

  // Process all piers for the sections to be selected
  for (const trackerRowId in storedBulkData.piers) {
    const pierArray = storedBulkData.piers[trackerRowId];
    if (!pierArray || !pierArray.length) continue;

    // Get data from first pier
    const firstPier = pierArray[0];
    const section = firstPier.section;

    // Skip if not in selected sections
    if (!sectionNumbers.includes(section)) {
      continue;
    }

    // Skip if already selected
    if (isTrackerRowSelected(newSelection, {
      tracker_row_id: trackerRowId
    })) {
      continue;
    }

    // Get section and tracker type keys
    const sectionKey = `section_${section}`;
    const trackerTypeKey = `trackerType_${firstPier.tracker_type_id.toString()}`;

    // If section doesn't exist, create it
    if (!newSelection.sections[sectionKey]) {
      newSelection.sections[sectionKey] = {
        totalCount: sectionCounts[sectionKey] || 0,
        selectedCount: 0,
        selectedRows: {}
      };
    }

    // If tracker type doesn't exist, create it
    if (!newSelection.trackerTypes[trackerTypeKey]) {
      newSelection.trackerTypes[trackerTypeKey] = {
        totalCount: trackerTypeCounts[trackerTypeKey] || 0,
        selectedCount: 0,
        selectedRows: {}
      };
    }

    // Add selected row to section
    newSelection.sections[sectionKey].selectedRows[trackerRowId] = {
      piers: storedBulkData.piers[trackerRowId],
      tt: storedBulkData.tt?.[trackerRowId] || []
    };
    newSelection.sections[sectionKey].selectedCount = (newSelection.sections[sectionKey].selectedCount || 0) + 1;

    // Add selected row to tracker type
    newSelection.trackerTypes[trackerTypeKey].selectedRows[trackerRowId] = {
      piers: storedBulkData.piers[trackerRowId],
      tt: storedBulkData.tt?.[trackerRowId] || []
    };
    newSelection.trackerTypes[trackerTypeKey].selectedCount = (newSelection.trackerTypes[trackerTypeKey].selectedCount || 0) + 1;

    // Increment total count
    newSelection.count++;
  }
  return newSelection;
};

// Handle select/unselect tracker type 

export const handleTrackerTypeSelection = (trackerTypeIds, prevTrackerTypes = []) => {
  // Ensure we have valid selection state
  const baseSelection = storedSelectedRows && typeof storedSelectedRows === 'object' ? storedSelectedRows : getInitialSelectionState();

  // Clone the current selection to create a new state
  const newSelection = {
    sections: {
      ...baseSelection.sections
    } || {},
    trackerTypes: {
      ...baseSelection.trackerTypes
    } || {},
    count: baseSelection.count || 0
  };
  if (!storedBulkData || !storedBulkData.piers) {
    return newSelection;
  }

  // Handle deselection of previous tracker types
  if (Array.isArray(prevTrackerTypes) && prevTrackerTypes.length > 0) {
    // Convert to strings for comparison
    const prevTypesStr = prevTrackerTypes.map(t => t.toString());

    // For each tracker type to deselect
    prevTypesStr.forEach(trackerTypeId => {
      const trackerTypeKey = `trackerType_${trackerTypeId}`;

      // If this tracker type exists in the selection, remove it
      if (newSelection.trackerTypes && newSelection.trackerTypes[trackerTypeKey]) {
        const rowsToRemove = Object.keys(newSelection.trackerTypes[trackerTypeKey].selectedRows || {});
        const rowCount = rowsToRemove.length;

        // Remove from sections
        rowsToRemove.forEach(trackerRowId => {
          const pierArray = storedBulkData.piers[trackerRowId];
          if (pierArray && pierArray.length > 0) {
            const firstPier = pierArray[0];
            const sectionKey = `section_${firstPier.section}`;
            if (newSelection.sections && newSelection.sections[sectionKey] && newSelection.sections[sectionKey].selectedRows) {
              delete newSelection.sections[sectionKey].selectedRows[trackerRowId];
              newSelection.sections[sectionKey].selectedCount--;
              if (newSelection.sections[sectionKey].selectedCount <= 0) {
                delete newSelection.sections[sectionKey];
              }
            }
          }
        });

        // Remove the entire tracker type
        delete newSelection.trackerTypes[trackerTypeKey];

        // Update count
        newSelection.count = Math.max(0, newSelection.count - rowCount);
      }
    });

    // Return early if no tracker types to add
    // if (!trackerTypeIds || trackerTypeIds.length === 0) {
    //   return newSelection;
    // }
  }

  // Only proceed with selection if there are tracker type IDs to select
  if (!trackerTypeIds || trackerTypeIds.length === 0) {
    return newSelection;
  }

  // Track total counts for sections and tracker types
  const sectionCounts = {};
  const trackerTypeCounts = {};

  // Calculate total counts for sections and tracker types
  for (const trackerRowId in storedBulkData.piers) {
    const pierArray = storedBulkData.piers[trackerRowId];
    if (!pierArray || !pierArray.length) continue;
    const firstPier = pierArray[0];
    const section = firstPier.section.toString();
    const trackerTypeId = firstPier.tracker_type_id;
    const sectionKey = `section_${section}`;
    const trackerTypeKey = `trackerType_${trackerTypeId}`;
    sectionCounts[sectionKey] = (sectionCounts[sectionKey] || 0) + 1;
    trackerTypeCounts[trackerTypeKey] = (trackerTypeCounts[trackerTypeKey] || 0) + 1;
  }

  // Process all piers for the tracker types to be selected
  for (const trackerRowId in storedBulkData.piers) {
    const pierArray = storedBulkData.piers[trackerRowId];
    if (!pierArray || !pierArray.length) continue;

    // Get data from first pier
    const firstPier = pierArray[0];
    const trackerTypeId = firstPier.tracker_type_id;

    // Skip if not in selected tracker types
    if (!trackerTypeIds.includes(trackerTypeId)) {
      continue;
    }

    // Skip if already selected
    if (isTrackerRowSelected(newSelection, {
      tracker_row_id: trackerRowId
    })) {
      continue;
    }

    // Get section and tracker type keys
    const sectionKey = `section_${firstPier.section.toString()}`;
    const trackerTypeKey = `trackerType_${trackerTypeId}`;

    // If section doesn't exist, create it
    if (!newSelection.sections[sectionKey]) {
      newSelection.sections[sectionKey] = {
        totalCount: sectionCounts[sectionKey] || 0,
        selectedCount: 0,
        selectedRows: {}
      };
    }

    // If tracker type doesn't exist, create it
    if (!newSelection.trackerTypes[trackerTypeKey]) {
      newSelection.trackerTypes[trackerTypeKey] = {
        totalCount: trackerTypeCounts[trackerTypeKey] || 0,
        selectedCount: 0,
        selectedRows: {}
      };
    }

    // Add selected row to section
    newSelection.sections[sectionKey].selectedRows[trackerRowId] = {
      piers: storedBulkData.piers[trackerRowId],
      tt: storedBulkData.tt?.[trackerRowId] || []
    };
    newSelection.sections[sectionKey].selectedCount = (newSelection.sections[sectionKey].selectedCount || 0) + 1;

    // Add selected row to tracker type
    newSelection.trackerTypes[trackerTypeKey].selectedRows[trackerRowId] = {
      piers: storedBulkData.piers[trackerRowId],
      tt: storedBulkData.tt?.[trackerRowId] || []
    };
    newSelection.trackerTypes[trackerTypeKey].selectedCount = (newSelection.trackerTypes[trackerTypeKey].selectedCount || 0) + 1;

    // Increment total count
    newSelection.count++;
  }
  return newSelection;
};

// Clear all selections

export const handleClearSelection = () => {
  // Ignore the current selection, always return a fresh empty state
  return {
    sections: {},
    trackerTypes: {},
    count: 0
  };
};

//Returns an array of selected sections by comparing totalCount is equal to selectedCount

export const getSelectedSections = () => {
  // If no selection state, return empty array
  if (!storedSelectedRows || !storedSelectedRows.sections) {
    return [];
  }
  const selectedSections = [];

  // Check each section to see if all rows in that section are selected
  for (const sectionKey in storedSelectedRows.sections) {
    const section = storedSelectedRows.sections[sectionKey];

    // Only add sections where the total count equals selected count
    if (section.totalCount > 0 && section.totalCount === section.selectedCount) {
      // Extract section number from the key (removing 'section_' prefix)
      const sectionNumber = sectionKey.replace('section_', '');

      // Convert to number if possible, otherwise use as string
      selectedSections.push(isNaN(Number(sectionNumber)) ? sectionNumber : Number(sectionNumber));
    }
  }
  return selectedSections;
};

//Returns an array of selected trackertypes by comparing totalCount is equal to selectedCount
export const getSelectedTrackerTypes = () => {
  // If no selection state, return empty array
  if (!storedSelectedRows || !storedSelectedRows.trackerTypes) {
    return [];
  }
  const selectedTrackerTypes = [];

  // Check each tracker type to see if all rows of that type are selected
  for (const trackerTypeKey in storedSelectedRows.trackerTypes) {
    const trackerType = storedSelectedRows.trackerTypes[trackerTypeKey];

    // Only add tracker types where the total count equals selected count
    if (trackerType.totalCount > 0 && trackerType.totalCount === trackerType.selectedCount) {
      // Extract tracker type ID from the key (removing 'trackerType_' prefix)
      const trackerTypeId = trackerTypeKey.replace('trackerType_', '');

      // Convert to number if possible, otherwise use as string
      selectedTrackerTypes.push(isNaN(Number(trackerTypeId)) ? trackerTypeId : Number(trackerTypeId));
    }
  }
  return selectedTrackerTypes;
};

// Returns an array of all selected tracker row IDs

export const getSelectedTrackerRows = () => {
  // If no selection state, return empty array
  if (!storedSelectedRows || !storedSelectedRows.sections) {
    return [];
  }
  const selectedTrackerRows = [];

  // Collect tracker row IDs from all sections
  for (const sectionKey in storedSelectedRows.sections) {
    const section = storedSelectedRows.sections[sectionKey];
    if (section.selectedRows) {
      // Get all tracker row IDs from this section
      const rowIds = Object.keys(section.selectedRows);

      // Convert to numbers if possible
      for (const rowId of rowIds) {
        selectedTrackerRows.push(isNaN(Number(rowId)) ? rowId : Number(rowId));
      }
    }
  }
  return selectedTrackerRows;
};

// Main selection handler function that manages all selection operations
export const handleSelectionChange = async (selectionType, selectionValue, prevSelection = []) => {
  // Ensure we have valid selection state
  const baseSelection = storedSelectedRows && typeof storedSelectedRows === 'object' ? storedSelectedRows : getInitialSelectionState();
  if (selectionType === 'individualTracker') {
    // For individual tracker selection, selectionValue should be the tracker data object
    return handleSelectTracker(selectionValue);
  } else if (selectionType === 'section') {
    // For section selection, selectionValue should be array of section numbers
    return handleSectionSelection(selectionValue, prevSelection);
  } else if (selectionType === 'trackerType') {
    // For tracker type selection, selectionValue should be array of tracker type IDs
    return handleTrackerTypeSelection(selectionValue, prevSelection);
  } else if (selectionType === 'selectAll') {
    // For select all, selectionValue is a boolean (true = select all, false = clear all)
    let newSelection = await handleSelectAll();
    return newSelection;
  } else if (selectionType === 'clear') {
    // For explicit clear
    return handleClearSelection();
  } else {
    // Unknown selection type, return current selection unchanged
    console.warn('Unknown selection type:', selectionType);
    return baseSelection;
  }
};